
import { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { btn, cx } from '@/lib/ui'
import { useModal } from '@/lib/use-modal'

/**
 * Stops a canvas full of unsaved work disappearing behind an in-app link.
 *
 * `beforeunload` only fires on a real browser unload — closing the tab,
 * reloading, leaving the origin. Every link inside the app is a client-side
 * navigation, which it never sees, so the most natural move after drawing a
 * rig ("let me look at the cable list") threw the rig away: the run sheet read
 * the saved, empty project and the canvas remounted from the server.
 *
 * This catches the click in the capture phase, anywhere on the page (the
 * workspace rail is outside the canvas tree), and asks. Saving is not
 * automatic on purpose: reloading without saving is the only way to abandon a
 * bad edit, and an autosave would quietly take that away.
 */
export function UnsavedGate({
  dirty,
  save,
}: {
  dirty: boolean
  /** Resolves true when the canvas is safely on the server. */
  save: () => Promise<boolean>
}) {
  const router = useRouter()
  const [href, setHref] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const dialogRef = useModal<HTMLDivElement>({
    open: href !== null,
    onClose: () => !busy && setHref(null),
  })

  // Read through a ref inside the listener so the handler can stay bound for
  // the life of the component instead of re-subscribing on every edit.
  const dirtyRef = useRef(dirty)
  useEffect(() => {
    dirtyRef.current = dirty
  }, [dirty])

  useEffect(() => {
    function onClick(event: MouseEvent) {
      if (!dirtyRef.current) return
      // Leave anything that is not a plain left click alone: cmd/ctrl/shift
      // click and middle click all open somewhere else and lose nothing.
      if (event.defaultPrevented || event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

      const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return

      const url = new URL(anchor.href, window.location.href)
      if (url.origin !== window.location.origin) return
      if (url.pathname === window.location.pathname && url.search === window.location.search) return

      event.preventDefault()
      event.stopPropagation()
      setHref(url.pathname + url.search)
    }

    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [])

  const go = useCallback(
    (target: string) => {
      // The gate is closed before navigating so it cannot catch its own push.
      setHref(null)
      setBusy(false)
      router.push(target)
    },
    [router],
  )

  if (!href) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-[var(--scrim)] p-4 backdrop-blur-[2px] sm:pt-[18vh]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) setHref(null)
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !busy) setHref(null)
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="unsaved-title"
        className="panel w-full max-w-md rounded-[var(--radius-card)] p-5 shadow-[var(--shadow-float)] sm:p-6"
      >
        <h2 id="unsaved-title" className="text-base font-semibold tracking-tight">
          Save this canvas first?
        </h2>
        <p className="mt-1.5 text-sm text-muted">
          You have changes that are not saved yet. Leaving this page without saving discards them.
        </p>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={() => setHref(null)}
            disabled={busy}
            className={cx(btn.base, btn.ghost)}
          >
            Stay here
          </button>
          <button
            type="button"
            onClick={() => go(href)}
            disabled={busy}
            className={cx(btn.base, btn.secondary)}
          >
            Discard and go
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={async () => {
              setBusy(true)
              const saved = await save()
              // A failed save keeps the page: the error sits in the toolbar and
              // the work is still on screen, which is the whole point.
              if (saved) go(href)
              else {
                setBusy(false)
                setHref(null)
              }
            }}
            className={cx(btn.base, btn.primary)}
          >
            {busy ? 'Saving…' : 'Save and go'}
          </button>
        </div>
      </div>
    </div>
  )
}
